/**
 * Pure, dependency-free normalisation for tenant icon markup supplied by runtime
 * configuration. Deliberately duplicated at apps/aos-config-manager/src/lib/svgNormalize.ts
 * because config-manager is import-isolated from the workspace; svgNormalize.parity.spec.ts
 * asserts the two copies stay byte-identical.
 */

export type PaintClass = 'INK' | 'NONE' | 'ABSENT'

const NON_INK_PAINTS = new Set(['none', 'transparent', 'inherit', 'unset', 'initial'])

const PAINT_ATTRIBUTE = /\b(fill|stroke)\s*=\s*(["'])(.*?)\2/g
const ID_ATTRIBUTE = /\bid\s*=\s*(["'])(.*?)\1/g
const ID_REFERENCE = /\b(?:xlink:href|href)\s*=\s*(["'])#(.*?)\1|url\(\s*#([^)\s]+)\s*\)/g
const OPENING_SVG_TAG = /<svg\b[^>]*>/

export const paintClass = (value: string | undefined | null): PaintClass => {
    if (value === undefined || value === null) {
        return 'ABSENT'
    }
    const normalized = value.trim().toLowerCase()
    if (NON_INK_PAINTS.has(normalized)) {
        return 'NONE'
    }
    return 'INK'
}

const isReference = (value: string) => value.trim().toLowerCase().startsWith('url(')

export const markupHash = (markup: string): string => {
    let hash = 0x811c9dc5
    for (let i = 0; i < markup.length; i++) {
        hash ^= markup.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193) >>> 0
    }
    return hash.toString(36)
}

const toCurrentColor = (markup: string) =>
    markup.replace(PAINT_ATTRIBUTE, (match, attribute, quote, value) => {
        if (paintClass(value) !== 'INK' || isReference(value) || value.trim() === 'currentColor') {
            return match
        }
        return `${attribute}=${quote}currentColor${quote}`
    })

/**
 * Gluestack's icon plumbing forwards the resolved colour as a stroke on the root, which would
 * outline fill-only glyphs. Only safe to neutralise when nothing in the glyph strokes on its own,
 * since neutralising the root would otherwise erase stroke-drawn art.
 *
 * Two separate reasons to leave the markup alone, and they need separate tests. If the root already
 * declares a stroke there is nothing to add - and testing the whole markup for that would find a
 * child's `stroke="none"` too, a common Figma export, after which a fill-only glyph kept getting
 * outlined. If any stroke actually paints, the glyph is stroke-drawn and the root must stay open for
 * it. `stroke="none"` on a child is neither: it paints nothing, so it must not suppress the root.
 */
const neutralizeRootStroke = (markup: string) => {
    const root = markup.match(OPENING_SVG_TAG)?.[0] ?? ''
    if (/\bstroke\s*=/.test(root)) {
        return markup
    }
    // exec loop rather than matchAll, for the lib es2019 reason given in prefixIds below
    const paintPattern = new RegExp(PAINT_ATTRIBUTE.source, PAINT_ATTRIBUTE.flags)
    let paint: RegExpExecArray | null
    while ((paint = paintPattern.exec(markup)) !== null) {
        if (paint[1] === 'stroke' && paintClass(paint[3]) === 'INK') {
            return markup
        }
    }
    return markup.replace(OPENING_SVG_TAG, tag => `${tag.slice(0, -1)} stroke="none">`)
}

/**
 * A prefix this function already applied: `markupHash` is base36 of a uint32, so at most 7 chars of
 * [0-9a-z] followed by the original id. Figma's own ids separate with underscores
 * (`clip0_395_4491`), so they do not match and still get prefixed.
 */
const ALREADY_PREFIXED = /^[0-9a-z]{1,7}-.+/

const prefixIds = (markup: string, prefix: string) => {
    const declared = new Set<string>()
    // exec loop rather than matchAll: the shared tsconfig targets lib es2019, and consumers that
    // pull this file in transitively (aos-helpers, aos-components) have no lib override, so
    // String.prototype.matchAll is not in their type environment.
    const idPattern = new RegExp(ID_ATTRIBUTE.source, ID_ATTRIBUTE.flags)
    let declaration: RegExpExecArray | null
    while ((declaration = idPattern.exec(markup)) !== null) {
        declared.add(declaration[2])
    }
    if (declared.size === 0) {
        return markup
    }
    // Skip ids this function already prefixed. Bulk import re-runs normalisation over
    // helm/<tenant>/common/icons, which is saved gate output, so without this the prefix stacks on
    // every re-seed - and it never converges, so a re-seeded glyph never matches the committed
    // bytes again. Skipping keeps the id byte-stable instead of minting a new prefix.
    //
    // Trade-off: a hand-authored id shaped like a prefix (`icon-foo`) is skipped too, so it no
    // longer gets silently disambiguated. A genuine collision then surfaces as the cross-glyph id
    // error in api/validation.ts instead, which is the louder failure of the two.
    const rename = (id: string) =>
        declared.has(id) && !ALREADY_PREFIXED.test(id) ? `${prefix}-${id}` : id
    return markup
        .replace(ID_ATTRIBUTE, (_match, quote, id) => `id=${quote}${rename(id)}${quote}`)
        .replace(ID_REFERENCE, (match, _quote, hrefId, urlId) =>
            hrefId !== undefined
                ? match.replace(`#${hrefId}`, `#${rename(hrefId)}`)
                : `url(#${rename(urlId)})`,
        )
}

/**
 * Replaces concrete ink with `currentColor` so the icon obeys its consumer's colour token, leaving
 * `none` and reference paints, geometry, `fill-rule`/`clip-rule` and element order untouched.
 *
 * Idempotent, so it is safe to apply at render time as a guard against markup that reached config
 * without passing through the authoring gate.
 */
export const normalizeIconInk = (markup: string): string =>
    neutralizeRootStroke(toCurrentColor(markup.trim()))

/**
 * Full authoring-time normalisation: ink, plus per-glyph id prefixing.
 *
 * NOT idempotent — the id prefix stacks — so this runs once, when artwork is accepted. Render paths
 * use `normalizeIconInk` instead.
 */
export const normalizeIconMarkup = (markup: string): string => {
    const trimmed = markup.trim()
    return prefixIds(normalizeIconInk(trimmed), markupHash(trimmed))
}
